import React, { type ComponentType } from 'react';

import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { RovoServiceAgentWidgetInjector } from '@atlassian/rovo-service-agent-chat-widget';

const ENHANCE_JSM_WIDGET_STABILITY = 'enhance_jsm_widget_stability';

const isEnhancedJsmWidgetStabilityEnabled = (): boolean =>
	FeatureGates.checkGate(ENHANCE_JSM_WIDGET_STABILITY);

/**
 * The Rovo service agent widget for the help center surface.
 *
 * Whether an agent is actually configured is not decided here. The widget script reads the surface
 * configuration for the container ARI it is given and stays hidden unless an enabled agent comes
 * back, and every page of a help center shares that one container. So each page can mount this and
 * get the same tenant-level answer without querying for the agent itself.
 */
export const HelpCenterRovoWidget = () => (
	<RovoServiceAgentWidgetInjector
		surface="HELP_CENTER"
		siteId={getCloudId()}
		widgetId={getHelpCenterAri()}
	/>
);

/**
 * Adds the help center Rovo widget to a page that does not render it itself.
 */
export const withHelpCenterRovoWidget = <Props extends object>(Page: ComponentType<Props>) => {
	const PageWithHelpCenterRovoWidget = (props: Props) => (
		<>
			<Page {...props} />
			{isEnhancedJsmWidgetStabilityEnabled() && <HelpCenterRovoWidget />}
		</>
	);

	PageWithHelpCenterRovoWidget.displayName = `withHelpCenterRovoWidget(${
		Page.displayName || Page.name || 'Component'
	})`;

	return PageWithHelpCenterRovoWidget;
};
